import {
  ComponentProps,
  ComponentType,
  ElementType,
  HTMLAttributes,
  MouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
} from 'react';

import { Placement, autoUpdate, flip, size, useFloating } from '@floating-ui/react-dom';
import { Menu as HeadlessMenu, Portal } from '@headlessui/react';
import { HTMLMotionProps, motion } from 'framer-motion';
import styled from 'styled-components';

import { FixedFullscreenOverlay } from '@components/fixed-fullscreen-overlay';
import { IconProps } from '@components/icons';
import { Link, LinkProps } from '@components/link';
import {
  handlePopoverSizeChange,
  moveUpMiddleware,
} from '@components/popover-button/popover-button';
import { Stack } from '@components/stack';
import { Switch, SwitchProps } from '@components/switch';
import { dropdownMotionConfig } from '@styles/animation';
import { theme } from '@styles/theme';
import { scrollIntoView } from '@utils/browser';
import { classNames } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { StrictUnion } from '@utils/types';

import { Button, ButtonProps } from '../button';
import { Tooltip } from '../tooltip';

export const HEADLESS_PORTAL_ID = 'headlessui-portal-id';
interface MenuPropsBase extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode | (({ close }: { close(): void }) => ReactNode);
  maxWidth?: number | string;
  width?: number | string;
  placement?: Placement;
  motionProps?: HTMLMotionProps<any>;
  maxHeight?: number;
  dataTestid?: string;
}

export type MenuProps<T extends ElementType = typeof Button> = {
  skipPortalWrapper?: boolean;
} & (MenuPropsBase &
  StrictUnion<
    | {
        buttonProps: ButtonProps;
      }
    | {
        trigger: T;
        triggerProps?: ComponentProps<T>;
      }
  >);

export function Menu<T extends ElementType = typeof Button>(props: MenuProps<T>) {
  const {
    children,
    placement = 'bottom-end',
    maxWidth,
    width,
    motionProps = dropdownMotionConfig,
    skipPortalWrapper = false,
    maxHeight,
    dataTestid,
    className,
    ...rest
  } = props;

  const { x, y, refs, strategy } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      flip({
        fallbackPlacements: ['left-start', 'right-start'],
      }),
      moveUpMiddleware,
      size({
        apply: handlePopoverSizeChange,
      }),
    ],
  });

  const trigger = props.buttonProps ? Button : props.trigger;
  const triggerProps = props.buttonProps || props.triggerProps;

  const menuItems = (close: () => void) => (
    <StyledMenuItems
      ref={refs.setFloating}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
      }}
      {...motionProps}
      $maxWidth={maxWidth}
      $maxHeight={maxHeight}
      $width={width}
    >
      <HeadlessMenu.Items static style={{ outline: 'none' }}>
        {typeof children === 'function' ? children({ close }) : children}
      </HeadlessMenu.Items>
    </StyledMenuItems>
  );
  return (
    <MenuContainer {...rest} className={classNames(className, 'menu-container')}>
      <HeadlessMenu>
        {({ open, close }) => (
          <>
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <HeadlessMenu.Button
                className="menu-button"
                // (@siggirh): Seems like the ForwardRefExoticComponent is causing issues here
                as={trigger as ComponentType<ButtonProps>}
                ref={refs.setReference}
                {...triggerProps}
                data-active={open}
                data-testid={dataTestid}
              />
            </span>
            {open &&
              (skipPortalWrapper ? (
                menuItems(close)
              ) : (
                <Portal>
                  <div id={HEADLESS_PORTAL_ID}>
                    {menuItems(close)}
                    <FixedFullscreenOverlay onClick={close} />
                  </div>
                </Portal>
              ))}
          </>
        )}
      </HeadlessMenu>
    </MenuContainer>
  );
}

const MenuContainer = styled.div({
  position: 'relative',
});

export const MenuSeparator = () => <StyledMenuSeparator aria-hidden />;

export type MenuItemProps = {
  onSelect?: (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
  label: any;
  icon?: ReactElement<IconProps>;
  extraIcon?: ReactElement<IconProps>;
  extraIconFullOpacity?: boolean;
  iconRight?: ReactNode;
  isDisabled?: boolean;
  variant?: 'base' | 'danger' | 'muted';
  selectionType?: 'close' | 'open' | 'through';
  isActive?: boolean;
  isHeader?: boolean;
  tooltip?: string;
  dataCy?: string;
  dataTestId?: string;
  helpText?: string;
};

export const MenuItem = ({
  onSelect,
  label,
  icon,
  iconRight = null,
  isDisabled = false,
  isHeader = false,
  variant = 'base',
  selectionType = 'close',
  isActive = false,
  extraIcon,
  extraIconFullOpacity = false,
  tooltip,
  dataCy,
  helpText,
}: MenuItemProps) => {
  const selectedMenuItemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = selectedMenuItemRef.current;
    if (isActive && element) {
      setTimeout(() => {
        scrollIntoView(element, { block: 'nearest' });
      });
    }
  }, [isActive]);

  return (
    <Tooltip content={tooltip}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <HeadlessMenu.Item>
          {({ active }) => (
            <StyledMenuItemContainer
              ref={isActive ? selectedMenuItemRef : undefined}
              data-header={isHeader}
              data-active={isActive}
              data-variant={variant}
              data-disabled={isDisabled}
              data-selected={active}
              data-cy={dataCy}
              $helpText={helpText}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                  if (selectionType === 'open') {
                    e.preventDefault();
                  }
                  onSelect?.(e);
                } else {
                  e.preventDefault();
                }
              }}
            >
              <>
                <StyledMenuItem $helpText={helpText} data-disabled={isDisabled}>
                  {icon && <MenuItemIcon>{icon}</MenuItemIcon>}
                  <LabelContainer>
                    {label}
                    {iconRight}
                  </LabelContainer>
                  {extraIcon && (
                    <RightMenuItemIcon data-full-opacity={Boolean(extraIconFullOpacity)}>
                      {extraIcon}
                    </RightMenuItemIcon>
                  )}
                </StyledMenuItem>
                {helpText && <HelpText>{helpText}</HelpText>}
              </>
            </StyledMenuItemContainer>
          )}
        </HeadlessMenu.Item>
      </motion.div>
    </Tooltip>
  );
};

type MenuItemLinkProps = MenuItemProps & { isExternal?: boolean } & Pick<LinkProps, 'to'>;

export const MenuItemLink = ({ to, isExternal, ...rest }: MenuItemLinkProps) => {
  return (
    <Link to={to} {...(isExternal ? { target: '_blank' } : {})}>
      <MenuItem {...rest} />
    </Link>
  );
};

const LabelContainer = styled.div({
  display: 'flex',
  flex: 1,
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const HelpText = styled.div({
  font: theme.font.regular14,
  color: theme.color.textHelper,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const MenuLabel = ({
  label,
  icon,
}: {
  label: ReactNode;
  icon?: ReactElement<IconProps>;
}) => {
  if (icon) {
    return (
      <StyledMenuLabel>
        <Stack space={2} orientation="horizontal">
          {icon}
          {label}
        </Stack>
      </StyledMenuLabel>
    );
  }
  return <StyledMenuLabel>{label}</StyledMenuLabel>;
};

export const MenuDescription = ({ text, info = true }: { text: ReactNode; info?: boolean }) => {
  return <StyledMenuDescription info={info}>{text}</StyledMenuDescription>;
};

type MenuSwitchProps = Omit<MenuItemProps, 'iconRight' | 'selectionType' | 'onSelect'> &
  Pick<SwitchProps, 'value' | 'onChange' | 'size'>;

const MenuSwitch = ({ onChange, value, isDisabled, size = 'md', ...rest }: MenuSwitchProps) => {
  const handleChange = () => {
    onChange?.(!value);
  };
  return (
    <Menu.Item
      iconRight={
        <Switch
          label=""
          size={size}
          value={value}
          isDisabled={isDisabled}
          onChange={isDisabled ? undefined : onChange}
        />
      }
      selectionType="open"
      onSelect={isDisabled ? undefined : handleChange}
      isDisabled={isDisabled}
      {...rest}
    />
  );
};

Menu.Description = MenuDescription;
Menu.Item = MenuItem;
Menu.ItemLink = MenuItemLink;
Menu.Separator = MenuSeparator;
Menu.Label = MenuLabel;
Menu.Switch = MenuSwitch;

const StyledMenuLabel = styled.div({
  padding: '0.5rem',
  font: theme.font.bold14,
  color: theme.color.textPrimary,
  textTransform: 'uppercase',
});

const StyledMenuDescription = styled.div<{
  info: boolean;
}>((p) => ({
  padding: '0.5rem',
  font: theme.font.regular14,
  color: p.info ? theme.color.textHelper : theme.color.textPrimary,
  whiteSpace: 'break-spaces',
}));

const StyledMenuSeparator = styled.div({
  height: '0.0625rem',
  backgroundColor: theme.color.border10,
  width: '100%',
  margin: `${pxToRem(8)} 0`,
});

const MenuItemIcon = styled.div({
  height: '1.5rem',
  width: '1.5rem',
  flexShrink: 0,
});
const RightMenuItemIcon = styled(MenuItemIcon)({
  height: 'auto',
  width: 'auto',
});

const StyledMenuItems = styled(motion.div)<{
  $maxWidth?: MenuProps['maxWidth'];
  $maxHeight?: MenuProps['maxHeight'];
  $width?: MenuProps['width'];
}>((p) => ({
  position: 'relative',
  overflow: 'auto',
  backgroundColor: theme.color.backgroundLightDark,
  boxShadow: theme.shadow.high,
  zIndex: 10000,
  padding: '1rem',
  borderRadius: '0.25rem',
  outline: 'none',
  width: p.$width || '16rem',
  maxWidth: p.$maxWidth,
  maxHeight: p.$maxHeight,
  whiteSpace: 'nowrap',
}));

const StyledMenuItemContainer = styled.div<{
  $helpText?: MenuItemProps['helpText'];
}>((p) => ({
  minHeight: theme.base.height,
  font: p.$helpText ? theme.font.semibold14 : theme.font.regular14,
  color: theme.color.textPrimary,
  padding: p.$helpText ? '0.5rem' : '0 0.5rem',
  borderRadius: '0.25rem',
  '--icon-color': theme.color.elementPurpleInvert,

  '&[data-variant="danger"]': {
    color: theme.color.red100,
    '--icon-color': theme.color.red100,
  },

  '&[data-selected="true"]': {
    outline: 'none',

    '&:not([data-variant="muted"])': {
      cursor: 'pointer',
    },

    '&[data-variant="base"]': {
      backgroundColor: theme.color.uiTrans5,
    },

    '&[data-variant="danger"]': {
      backgroundColor: theme.color.redTrans10,
    },
  },

  '&[data-disabled="true"]': {
    cursor: 'not-allowed !important',
  },

  '&[data-active="true"]': {
    color: theme.color.elementBlue100,
  },

  '&[data-header="true"]': {
    font: theme.font.bold14,
    color: theme.color.textPrimary,
    textTransform: 'uppercase',
  },
}));

const StyledMenuItem = styled.div<{
  $helpText?: MenuItemProps['helpText'];
}>((p) => ({
  minHeight: p.$helpText ? 'auto' : theme.base.height,
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  '& > div': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '[data-disabled="true"] &': {
    '& > div:not([data-full-opacity="true"])': {
      opacity: 0.5,
    },
  },
}));
